import { cn } from "@/lib/utils";
import { MessageSquare } from "lucide-react";
import { ButtonHTMLAttributes, useEffect, useRef, useState } from "react";

interface CommentButtonProps extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "children"
> {
    /** 현재 답글 작성 중인지 여부 (활성화 상태) */
    active?: boolean;
    /** 답글 개수 */
    count?: number;
    /** 크기 variant */
    size?: "sm" | "md";
    /** 비활성화 여부 */
    disabled?: boolean;
    className?: string;
}

/**
 * 답글 버튼 컴포넌트
 * LoveReactionButton 패턴을 따라 일관된 스타일로 구현
 *
 * @example
 * ```tsx
 * <CommentButton
 *   active={isReplying}
 *   count={replyCount}
 *   onClick={handleReplyToggle}
 *   disabled={isSubmitting}
 * />
 * ```
 */
export const CommentButton: React.FC<CommentButtonProps> = ({
    active = false,
    count = 0,
    size = "sm",
    disabled = false,
    className,
    onClick,
    ...props
}) => {
    const [displayCount, setDisplayCount] = useState(count);
    const [isAnimating, setIsAnimating] = useState(false);
    const [direction, setDirection] = useState<"up" | "down">("up");
    const [wasJustClicked, setWasJustClicked] = useState(false);
    const prevCountRef = useRef(count);

    useEffect(() => {
        if (count !== prevCountRef.current) {
            setDirection(count > prevCountRef.current ? "up" : "down");
            setIsAnimating(true);

            const timer = setTimeout(() => {
                setDisplayCount(count);
                setIsAnimating(false);
            }, 150);

            prevCountRef.current = count;
            return () => clearTimeout(timer);
        }
    }, [count]);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        setWasJustClicked(true);
        setTimeout(() => setWasJustClicked(false), 300);
        onClick?.(e);
    };

    const sizeClasses = {
        sm: "h-7",
        md: "h-8"
    };

    const iconSize = size === "sm" ? "size-3.5" : "size-4";
    const textSize = size === "sm" ? "text-xs" : "text-sm";

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={disabled}
            className={cn(
                `group inline-flex cursor-pointer items-center gap-1.5
                rounded-md border px-2.5 py-1 font-medium transition-all
                duration-200`,
                sizeClasses[size],
                // 활성화 상태일 때 스타일 (blue 계열)
                active
                    ? `border-green-200 bg-blue-50 text-blue-600
                        hover:bg-blue-100 dark:border-blue-800
                        dark:bg-blue-900/20 dark:text-blue-400
                        dark:hover:bg-blue-900/30`
                    : `border-gray-900 bg-transparent text-gray-600
                        hover:bg-gray-100 dark:border-gray-700
                        dark:text-gray-400 dark:hover:bg-gray-800`,
                // 비활성화 상태
                disabled && "cursor-not-allowed opacity-50",
                !disabled && "active:scale-95",
                // 클릭 시 잠깐 스케일 업
                wasJustClicked && "scale-105",
                className
            )}
            {...props}
        >
            {/* 메시지 아이콘 */}
            <MessageSquare
                className={cn(
                    iconSize,
                    "transition-all duration-200",
                    active && "scale-110 fill-current"
                )}
            />

            {/* 카운트 표시 - 슬라이드 애니메이션 */}
            {(count > 0 || displayCount > 0) && (
                <span
                    className={cn(
                        textSize,
                        "min-w-[1ch] text-center transition-all duration-150",
                        isAnimating &&
                            direction === "up" &&
                            "translate-y-1 opacity-0",
                        isAnimating &&
                            direction === "down" &&
                            "-translate-y-1 opacity-0"
                    )}
                >
                    {displayCount}
                </span>
            )}
        </button>
    );
};
